import React from "react";
import { Link } from "react-router-dom";

import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import {
  aboutIntro,
  missionValues,
  companyHighlights,
  licenseInfo,
} from "../data/siteData";

function About() {
  return (
    <div className="page-shell">
      <SiteHeader />

      <main>

        {/* ── 1. PAGE HERO ────────────────────────────────────── */}
        <section className="page-hero">
          <div className="container">
            <nav className="breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span className="breadcrumb-sep">›</span>
              <span className="breadcrumb-current">About</span>
            </nav>
            <p className="eyebrow">{aboutIntro.eyebrow}</p>
            <h1>About Us</h1>
            <p className="about-hero-sub">
              PCN-licensed pharmaceutical wholesale distributor · Abuja, Nigeria
            </p>
          </div>
          <div className="about-hero-image-wrap">
            <img
              src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=1600&auto=format&fit=crop"
              alt="Pharmaceutical operations"
              className="about-hero-image"
            />
            <div className="about-hero-image-overlay" />
          </div>
        </section>

        {/* ── 2. INTRO ─────────────────────────────────────────── */}
        <section className="section" style={{ background: "var(--white)" }}>
          <div className="container about-intro-grid">

            <div className="about-intro-copy">
              <p className="eyebrow">{aboutIntro.eyebrow}</p>
              <h2>{aboutIntro.heading}</h2>
              <p>{aboutIntro.body}</p>
              <p style={{ marginTop: "1rem" }}>
                Based in Abuja and serving trade buyers across Nigeria, we
                combine structured product handling with responsive account
                support — giving procurement teams the clarity and continuity
                they need to operate efficiently.
              </p>
              <div className="btn-group" style={{ marginTop: "2rem" }}>
                <Link to="/contact" className="btn btn-primary">
                  Open a wholesale account
                </Link>
                <Link to="/services" className="btn btn-secondary">
                  Our services →
                </Link>
              </div>
            </div>

            <div className="about-intro-image-wrap">
              <img
                src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=1200&auto=format&fit=crop"
                alt="Pharmaceutical supply chain"
                className="about-intro-image"
              />
              <div className="about-intro-stat-card">
                <span className="about-intro-stat-value">RC</span>
                <span className="about-intro-stat-num">1727250</span>
                <span className="about-intro-stat-label">Registered in Nigeria</span>
              </div>
            </div>

          </div>
        </section>

        {/* ── 3. MISSION & VALUES ──────────────────────────────── */}
        <section className="section section-grey">
          <div className="container">
            <div className="section-heading centered">
              <p className="eyebrow">Mission & Values</p>
              <h2>The principles behind every transaction.</h2>
              <p>
                Our values are not aspirational statements — they are the
                operational standards we hold ourselves to on every order,
                every enquiry, and every account relationship.
              </p>
            </div>

            <div className="grid-4 about-values-grid">
              {missionValues.map((val) => (
                <article key={val.number} className="value-card">
                  <p className="value-card-number">{val.number}</p>
                  <h3>{val.title}</h3>
                  <p>{val.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* ── 4. COMPANY HIGHLIGHTS ────────────────────────────── */}
        <section className="section section-dark">
          <div className="container">
            <div className="section-heading">
              <p className="eyebrow">Why Etiquette Pharmaceuticals</p>
              <h2>What sets us apart as a wholesale partner.</h2>
            </div>

            <div className="grid-3 about-highlights-grid">
              {companyHighlights.map((item, i) => (
                <article key={item.title} className="about-highlight-card">
                  <span className="about-highlight-number">0{i + 1}</span>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </article>
              ))}
            </div>

            <div className="about-highlights-image-row">
              <img
                src="https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1200&auto=format&fit=crop"
                alt="Pharmaceutical distribution"
                className="about-highlights-image"
              />
              <img
                src="https://images.unsplash.com/photo-1563213126-a4273aed2016?w=1200&auto=format&fit=crop"
                alt="Medicine supply"
                className="about-highlights-image"
              />
            </div>
          </div>
        </section>

        {/* ── 5. REGULATORY / LICENCE ──────────────────────────── */}
        <section className="section" style={{ background: "var(--white)" }}>
          <div className="container about-licence-grid">

            <div className="about-licence-copy">
              <p className="eyebrow">Regulatory Standing</p>
              <h2>{licenseInfo.heading}</h2>
              <p>{licenseInfo.body}</p>

              <ul className="bullet-list" style={{ marginTop: "1.75rem" }}>
                {licenseInfo.points.map((point) => (
                  <li key={point} className="bullet-item">
                    <span className="bullet-dot" />
                    {point}
                  </li>
                ))}
              </ul>

              <div className="btn-group" style={{ marginTop: "2rem" }}>
                <Link to="/contact" className="btn btn-primary">
                  Speak with our team
                </Link>
              </div>
            </div>

            <div className="about-licence-card-wrap">
              <div className="licence-card">
                <div className="licence-badge">
                  <span className="licence-badge-text">PCN</span>
                </div>
                <h3>PCN Licensed</h3>
                <p>Pharmaceutical Council of Nigeria</p>
                <hr className="about-licence-divider" />
                <div className="about-licence-detail-row">
                  <span className="about-licence-detail-label">Company</span>
                  <span className="about-licence-detail-value">Etiquette Pharmaceuticals Limited</span>
                </div>
                <div className="about-licence-detail-row">
                  <span className="about-licence-detail-label">RC Number</span>
                  <span className="about-licence-detail-value">1727250</span>
                </div>
                <div className="about-licence-detail-row">
                  <span className="about-licence-detail-label">Licence Type</span>
                  <span className="about-licence-detail-value">Wholesale Distribution</span>
                </div>
                <div className="about-licence-detail-row">
                  <span className="about-licence-detail-label">Location</span>
                  <span className="about-licence-detail-value">Abuja, Nigeria</span>
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* ── 6. CTA BANNER ────────────────────────────────────── */}
        <section className="section section-green">
          <div className="container cta-banner">
            <p className="eyebrow" style={{ color: "rgba(255,255,255,0.6)" }}>
              Work with us
            </p>
            <h2>Open a wholesale account today.</h2>
            <p>
              Whether you are a pharmacy, hospital, clinic, or regional
              distributor — we are ready to support your supply needs.
            </p>
            <div className="btn-group" style={{ justifyContent: "center" }}>
              <Link to="/contact" className="btn btn-outline">
                Request a Quote
              </Link>
              <Link to="/products" className="btn btn-ghost" style={{ color: "rgba(255,255,255,0.8)" }}>
                View our products →
              </Link>
            </div>
          </div>
        </section>

      </main>

      <SiteFooter />
    </div>
  );
}

export default About;
